import closeSvg from "@/assets/close.svg";
import EnvelopeLight from "@/assets/envelopeLight.svg";
import { components } from "@/lib/ApiSchema";
import Button from "@/lib/Button";
import Input from "@/lib/Input";
import ProviderIcon from "@/lib/ProviderIcon";
import $api, { ErrorSchema } from "@/lib/queries/openapi";
import { getErrorMessage, getProviderName } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { PropsWithChildren, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useParams, useSearchParams } from "react-router";
import Select, { SingleValue } from "react-select";
import BanIcon from "~icons/fa6-solid/ban";
import CheckIcon from "~icons/fa6-solid/check";
import ClockIcon from "~icons/fa6-solid/clock";
import useModal from "../../lib/modal/useModal";

type OauthSubscriptionType = "microsoft_email";
type SubscriptionType = OauthSubscriptionType | "custom";

type SubscriptionTypeOption = {
  label: string;
  value: SubscriptionType;
};

const SUBSCRIPTION_TYPE_OPTIONS: SubscriptionTypeOption[] = [
  { label: "Microsoft Outlook", value: "microsoft_email" },
  { label: "Custom", value: "custom" },
];

function createCallbackUrl(
  options: {
    teamSlug: string;
  } & SubscriptionSearchParams,
): string {
  const url = new URL(
    `/teams/${options.teamSlug}/settings/email-accounts`,
    window.location.origin,
  );
  url.searchParams.set("provider", options.provider);
  if ("email" in options) {
    url.searchParams.set("email", options.email);
  } else {
    url.searchParams.set("subscription_id", options.subscription_id);
  }

  return url.toString();
}

type SubscriptionSearchParams =
  | {
      // Create a subscription for the provided email using the most recently added OAuth provider for the user
      provider: string;
      email: string;
      type: "create";
    }
  | {
      // Update a subscription by ID to use a different OAuth connection
      provider: string;
      subscription_id: string;
      type: "reconnect";
    };

/** Given the search params for this page, return the params for creating an OAuth based email subscription. */
function getSubscriptionSearchParams(
  searchParams: URLSearchParams,
): SubscriptionSearchParams | undefined {
  const provider = searchParams.get("provider");

  if (!provider) {
    return undefined;
  }

  const email = searchParams.get("email");

  if (email) {
    return {
      provider,
      email,
      type: "create",
    };
  }

  const subscriptionId = searchParams.get("subscription_id");

  if (subscriptionId) {
    return {
      provider,
      subscription_id: subscriptionId,
      type: "reconnect",
    };
  }
}

export default function EmailAccounts() {
  const { teamSlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const subscriptionSearchParams = useMemo(
    () => getSubscriptionSearchParams(searchParams),
    [searchParams],
  );

  const { showModal, closeModal } = useModal();

  const queryClient = useQueryClient();
  const { mutate: createSubscription } = $api.useMutation(
    "post",
    "/api/teams/{team_slug}/subscriptions",
    {
      onSuccess: () => {
        toast.success("Email account added successfully");
        queryClient.invalidateQueries({
          queryKey: ["get", "/api/teams/{team_slug}/subscriptions"],
        });
        closeModal();
        setSearchParams({});
      },
      onError: (error: ErrorSchema) => {
        toast.error(
          getErrorMessage(error) || "Failed to connect email account",
        );
      },
    },
  );
  const { mutate: reconnectSubscription } = $api.useMutation(
    "post",
    "/api/teams/{team_slug}/subscriptions/{id}/reconnect_oauth",
    {
      onSuccess: () => {
        toast.success("Email account reconnected successfully");
        queryClient.invalidateQueries({
          queryKey: ["get", "/api/teams/{team_slug}/subscriptions"],
        });
        setSearchParams({});
      },
    },
  );

  const { data: socialAccounts } = $api.useQuery(
    "get",
    "/api/_allauth/browser/v1/account/providers",
  );

  // OAuth based subscription creation
  useEffect(() => {
    if (!socialAccounts || subscriptionSearchParams?.type !== "create") {
      return;
    }

    createSubscription({
      params: { path: { team_slug: teamSlug! } },
      body: {
        provider_id: subscriptionSearchParams.provider,
        email: subscriptionSearchParams.email,
      },
    });
  }, [socialAccounts, subscriptionSearchParams, createSubscription, teamSlug]);

  // OAuth based subscription reconnection
  useEffect(() => {
    if (!socialAccounts || subscriptionSearchParams?.type !== "reconnect") {
      return;
    }

    reconnectSubscription({
      params: {
        path: {
          team_slug: teamSlug!,
          id: subscriptionSearchParams.subscription_id,
        },
      },
      body: { provider_id: subscriptionSearchParams.provider },
    });
  }, [
    socialAccounts,
    subscriptionSearchParams,
    reconnectSubscription,
    teamSlug,
  ]);

  const handleAccountTypeSelect = (
    option: SingleValue<SubscriptionTypeOption>,
  ) => {
    if (option) {
      showModal(
        <AddAccountModalContent
          providerLabel={option.label}
          closeModal={closeModal}
        >
          {option.value === "custom" ? (
            <AddAccountCustomContent closeModal={closeModal} />
          ) : (
            <AddAccountOauthContent
              closeModal={closeModal}
              provider={option.value}
            />
          )}
        </AddAccountModalContent>,
      );
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-bold">Email Accounts</h1>
        <div className="mt-4 max-w-80">
          <Select
            placeholder="Add Account"
            options={SUBSCRIPTION_TYPE_OPTIONS}
            onChange={handleAccountTypeSelect}
            className="text-sm min-w-80"
            value={null}
            styles={{
              control: (baseStyles) => ({
                ...baseStyles,
                borderStyle: "none",
              }),
              placeholder: (baseStyles) => ({
                ...baseStyles,
                color: "var(--color-slate-900)",
              }),
            }}
            menuPlacement="auto"
          />
        </div>
      </div>

      <div className="max-w-6xl">
        <SubscriptionList />
      </div>
    </div>
  );
}

function AddAccountModalContent({
  children,
  providerLabel,
  closeModal,
}: PropsWithChildren<{
  providerLabel: string;
  closeModal: () => void;
}>) {
  return (
    <div className="p-6 min-w-3xl flex flex-col gap-4">
      <div className="flex justify-between items-center gap-8">
        <h2 className="font-bold text-slate-900">
          Add {providerLabel} Email Account
        </h2>
        <button type="button" onClick={closeModal} className="cursor-pointer">
          <img src={closeSvg} alt="close" />
        </button>
      </div>

      {children}
    </div>
  );
}

function AddAccountOauthContent({
  closeModal,
  provider,
}: {
  closeModal: () => void;
  provider: OauthSubscriptionType;
}) {
  const [email, setEmail] = useState("");
  const { teamSlug } = useParams();

  return (
    <div className="flex flex-col gap-8">
      <Input
        label="Email Address to Connect to Uptool"
        type="email"
        value={email}
        onChange={setEmail}
        placeholder="rfq@roi-mfg.com"
        labelClassName="text-xs font-bold text-slate-900"
        required
        width={368}
      />

      <AddAccountModalFooter closeModal={closeModal}>
        <form
          method="POST"
          action="/api/_allauth/browser/v1/auth/provider/redirect"
        >
          <input type="hidden" name="provider" value={provider} />
          <input
            type="hidden"
            name="callback_url"
            value={createCallbackUrl({
              teamSlug: teamSlug!,
              provider,
              email,
              type: "create",
            })}
          />
          <input type="hidden" name="process" value="connect" />
          <Button primary size="lg" type="submit" disabled={!email}>
            Next
          </Button>
        </form>
      </AddAccountModalFooter>
    </div>
  );
}

/** Very loose validation for hostnames. */
const HOSTNAME_REGEXP = /^\S+$/;

function AddAccountCustomContent({ closeModal }: { closeModal: () => void }) {
  const { teamSlug } = useParams();
  const queryClient = useQueryClient();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [imapHost, setImapHost] = useState("");
  const [smtpHost, setSmtpHost] = useState("");

  const { mutate: createSubscription, isPending } = $api.useMutation(
    "post",
    "/api/teams/{team_slug}/subscriptions",
    {
      onSuccess: () => {
        toast.success("Email account added successfully");
        queryClient.invalidateQueries({
          queryKey: ["get", "/api/teams/{team_slug}/subscriptions"],
        });
        closeModal();
      },
      onError: (error: ErrorSchema) => {
        toast.error(getErrorMessage(error) || "Failed to add email account");
      },
    },
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    createSubscription({
      params: {
        path: { team_slug: teamSlug! },
      },
      body: {
        email,
        password,
        imap_host: imapHost,
        smtp_host: smtpHost,
      },
    });
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-8">
      <Input
        label="Email Address to Connect to Uptool"
        type="email"
        value={email}
        onChange={setEmail}
        placeholder="your.email@domain.com"
        labelClassName="text-xs font-bold text-slate-900"
        required
        width={368}
      />

      <Input
        label="IMAP"
        value={imapHost}
        onChange={setImapHost}
        placeholder="imap.gmail.com"
        labelClassName="text-xs font-bold text-slate-900"
        required
        width={368}
        pattern={HOSTNAME_REGEXP.source}
        title="Hostname cannot contain spaces"
      />

      <Input
        label="SMTP"
        value={smtpHost}
        onChange={setSmtpHost}
        placeholder="smtp.gmail.com"
        labelClassName="text-xs font-bold text-slate-900"
        required
        width={368}
        pattern={HOSTNAME_REGEXP.source}
        title="Hostname cannot contain spaces"
      />

      <div>
        <Input
          label="App Password / Secret"
          type="password"
          value={password}
          onChange={setPassword}
          labelClassName="text-xs font-bold text-slate-900"
          required
          width={368}
          placeholder={"•".repeat(16)}
        />
        <a
          href="https://docs.google.com/document/d/1izZcurT7iFaMcSxL0kxw9G6CnRhGQc7LIUOVa0s93Jk/edit?usp=sharing"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 underline text-[9px] mt-1 italic inline-block"
        >
          How to create Gmail app password
        </a>
      </div>

      <AddAccountModalFooter closeModal={closeModal}>
        <Button size="lg" type="submit" disabled={isPending} primary>
          Save
        </Button>
      </AddAccountModalFooter>
    </form>
  );
}

function AddAccountModalFooter({
  children,
  closeModal,
}: PropsWithChildren<{ closeModal: () => void }>) {
  return (
    <div className="flex justify-end items-center gap-4">
      <Button size="lg" secondary type="button" onClick={closeModal}>
        Cancel
      </Button>
      {children}
    </div>
  );
}

function SubscriptionList() {
  const { teamSlug } = useParams();
  const { data: subscriptions } = $api.useQuery(
    "get",
    "/api/teams/{team_slug}/subscriptions",
    { params: { path: { team_slug: teamSlug! } } },
  );

  return (
    <div className="flex flex-col gap-7">
      {subscriptions?.items.map((subscription) => (
        <SubscriptionCard key={subscription.id} data={subscription} />
      ))}
    </div>
  );
}

function SubscriptionStatus({
  subscription,
}: {
  subscription: components["schemas"]["SubscriptionListItemSchema"];
}) {
  if (subscription.imap_last_error !== null) {
    return (
      <div className="flex items-center gap-2">
        <BanIcon className="size-6 text-red-4" />
        <p className="text-sm">{subscription.imap_last_error}</p>
      </div>
    );
  }

  if (subscription.imap_last_checked === null) {
    return (
      <div className="flex items-center gap-2">
        <ClockIcon className="size-6 text-yellow-500" />
        <p className="text-sm">Pending verification</p>
      </div>
    );
  }

  return <CheckIcon className="size-6 text-[#72d358]" />;
}

function SubscriptionCard({
  data,
}: {
  data: components["schemas"]["SubscriptionListItemSchema"];
}) {
  return (
    <div className="bg-white rounded-lg py-4 px-6">
      <div className="flex justify-between gap-16">
        <div className="flex flex-col gap-6">
          <h3 className="font-bold flex items-center gap-2">
            {data.provider === "custom" ? (
              <img alt="Custom" src={EnvelopeLight} className="size-6" />
            ) : (
              <ProviderIcon provider={data.provider} className="size-6" />
            )}
            {getProviderName(data.provider)}
          </h3>

          <div className="flex gap-16">
            <div className="flex flex-col gap-2 text-sm min-w-40">
              <h4 className="font-bold">Email</h4>
              <p>{data.email}</p>
            </div>

            <div className="flex flex-col gap-2 text-sm min-w-40">
              <h4 className="font-bold">Last checked</h4>
              <p>
                {data.imap_last_checked
                  ? dayjs(data.imap_last_checked).fromNow()
                  : "never"}
              </p>
            </div>

            <div className="flex flex-col gap-2 text-sm min-w-40">
              <h4 className="font-bold">Status</h4>
              <SubscriptionStatus subscription={data} />
            </div>
          </div>

          <div className="flex gap-16">
            {data.imap_host && (
              <div className="flex flex-col gap-2 text-sm min-w-40">
                <h4 className="font-bold">IMAP Server</h4>
                <p>{data.imap_host}</p>
              </div>
            )}

            {data.smtp_host && (
              <div className="flex flex-col gap-2 text-sm min-w-40">
                <h4 className="font-bold">SMTP Server</h4>
                <p>{data.smtp_host}</p>
              </div>
            )}

            {data.authorizing_account && (
              <div className="flex flex-col gap-2 text-sm min-w-40">
                <h4 className="font-bold">Authorizing User</h4>
                <div className="flex items-center gap-2">
                  <p>
                    Authorized by {data.authorizing_account.display_name}
                    {data.authorizing_account.external_email &&
                      ` using ${data.authorizing_account.external_email}`}
                  </p>

                  <ReconnectOauthButton subscription={data} />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end items-end">
          <DeleteSubscriptionButton subscription={data} />
        </div>
      </div>
    </div>
  );
}

function DeleteSubscriptionButton({
  subscription,
}: {
  subscription: Pick<components["schemas"]["SubscriptionListItemSchema"], "id">;
}) {
  const { teamSlug } = useParams();
  const queryClient = useQueryClient();

  const { mutate: deleteSubscription, isPending: isDeleting } =
    $api.useMutation("delete", "/api/teams/{team_slug}/subscriptions/{id}", {
      onSuccess: () => {
        toast.success("Email account removed successfully");
        queryClient.invalidateQueries({
          queryKey: ["get", "/api/teams/{team_slug}/subscriptions"],
        });
      },
      onError: (error: ErrorSchema) => {
        toast.error(getErrorMessage(error) || "Failed to remove email account");
      },
    });

  return (
    <Button
      danger
      onClick={() =>
        deleteSubscription({
          params: { path: { team_slug: teamSlug!, id: subscription.id } },
        })
      }
      disabled={isDeleting}
    >
      Remove Email Account
    </Button>
  );
}

function ReconnectOauthButton({
  subscription,
}: {
  subscription: Pick<
    components["schemas"]["SubscriptionListItemSchema"],
    "provider" | "id"
  >;
}) {
  const { teamSlug } = useParams();

  return (
    <form
      method="POST"
      action="/api/_allauth/browser/v1/auth/provider/redirect"
    >
      <input type="hidden" name="provider" value={subscription.provider} />
      <input
        type="hidden"
        name="callback_url"
        value={createCallbackUrl({
          teamSlug: teamSlug!,

          type: "reconnect",
          provider: subscription.provider,
          subscription_id: subscription.id,
        })}
      />
      <input type="hidden" name="process" value="connect" />
      <button
        type="submit"
        className="text-slate-500 border-slate-500 cursor-pointer rounded-md border px-2"
      >
        Reconnect
      </button>
    </form>
  );
}
